import React, { useMemo, useState } from 'react';
import StatusPill from './StatusPill';

const statusLabels = {
  not_started: 'No iniciado',
  in_training: 'En formación',
  ready_to_practice: 'Listo para practicar',
  ready_with_supervision: 'Listo con supervisión',
};

function errorDetail(detail) {
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || String(item)).join(' · ');
  }
  if (detail && typeof detail === 'object') return detail.message;
  return detail;
}

export default function TeamReadinessPanel({ apiUrl, groups, templates, onReload }) {
  const [openGroupId, setOpenGroupId] = useState(groups[0]?.id || null);
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [certifiedFilter, setCertifiedFilter] = useState('');
  const [pendingUnitFilter, setPendingUnitFilter] = useState('');
  const [selected, setSelected] = useState([]);
  const [form, setForm] = useState({ title: '', body: '', link_path: '/learn' });
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const group = groups.find((item) => item.id === openGroupId) || groups[0];

  const members = useMemo(() => {
    if (!group) return [];
    return group.members.filter((member) => (
      (!roleFilter || member.roles.includes(roleFilter))
      && (!statusFilter || member.readiness_status === statusFilter)
      && (!certifiedFilter || String(member.certified_current) === certifiedFilter)
      && (!pendingUnitFilter || member.pending_unit_ids.includes(pendingUnitFilter))
    ));
  }, [certifiedFilter, group, pendingUnitFilter, roleFilter, statusFilter]);

  if (!groups.length) return null;

  const useTemplate = (templateId) => {
    const template = templates.find((item) => item.id === templateId);
    if (template) setForm({ title: template.title, body: template.body, link_path: template.link_path });
  };

  const toggle = (userId) => setSelected((prev) => (
    prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
  ));

  const send = async (event) => {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      const response = await fetch(`${apiUrl}/api/readiness/groups/${group.id}/notifications`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, recipient_ids: selected.length ? selected : null }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(errorDetail(data.detail) || 'No se pudo enviar el mensaje.');
      setMessage({ type: 'success', text: `Mensaje enviado a ${data.recipient_count} integrante(s).` });
      setSelected([]);
      await onReload();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setBusy(false);
    }
  };

  const roles = [...new Set(group.members.flatMap((member) => member.roles))];
  const pendingUnits = [...new Set(group.members.flatMap((member) => member.pending_unit_ids))];
  return (
    <section className="forge-panel team-readiness-panel">
      <div className="learning-section-head">
        <div><div className="forge-section-eyebrow">Responsable</div><h2>Preparación del equipo</h2></div>
        <select value={group.id} onChange={(event) => { setOpenGroupId(Number(event.target.value)); setSelected([]); }}>
          {groups.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
      </div>
      <div className="team-summary-grid">
        <div><span>Integrantes</span><strong>{group.summary.total}</strong></div>
        <div><span>Avance medio</span><strong>{group.summary.average_completion_pct}%</strong></div>
        <div><span>Listos con supervisión</span><strong>{group.summary.status_counts.ready_with_supervision || 0}</strong></div>
      </div>
      {Object.keys(group.summary.pending_unit_counts || {}).length > 0 && <div className="team-needs"><span>Necesidades principales</span>{Object.entries(group.summary.pending_unit_counts).sort((a, b) => b[1] - a[1]).slice(0, 4).map(([unitId, count]) => <small key={unitId}>{unitId} · {count}</small>)}</div>}
      {group.summary.unit_progress?.length > 0 && <div className="team-unit-progress"><span>Avance por unidad</span>{group.summary.unit_progress.map((unit) => <div key={unit.unit_id}><small>{unit.unit_id}</small><i><b style={{ width: `${unit.completion_pct}%` }} /></i><strong>{unit.completion_pct}%</strong></div>)}</div>}
      <div className="team-filters">
        <select value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)}><option value="">Todos los roles</option>{roles.map((role) => <option key={role}>{role}</option>)}</select>
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><option value="">Todos los estados</option>{Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
        <select value={certifiedFilter} onChange={(event) => setCertifiedFilter(event.target.value)}><option value="">Certificación: todas</option><option value="true">Vigente</option><option value="false">Pendiente</option></select>
        <select value={pendingUnitFilter} onChange={(event) => setPendingUnitFilter(event.target.value)}><option value="">Todas las unidades pendientes</option>{pendingUnits.map((unitId) => <option key={unitId} value={unitId}>{unitId}</option>)}</select>
      </div>
      <div className="team-member-list">
        {members.map((member) => (
          <label key={member.user_id} className="team-member-row">
            <input type="checkbox" checked={selected.includes(member.user_id)} onChange={() => toggle(member.user_id)} />
            <span className="team-member-name"><strong>@{member.username}</strong><small>{member.roles.join(', ')}</small></span>
            <span className="team-member-progress"><i style={{ width: `${member.completion_pct}%` }} /><small>{member.completion_pct}% ruta · {member.workshop_completion_pct}% taller ({member.workshop_status})</small></span>
            <StatusPill color={member.readiness_status === 'ready_with_supervision' ? 'green' : member.readiness_status === 'not_started' ? 'amber' : 'blue'}>{statusLabels[member.readiness_status]}</StatusPill>
          </label>
        ))}
      </div>
      <form className="team-message-form" onSubmit={send}>
        <h3>Enviar acompañamiento</h3>
        <select defaultValue="" onChange={(event) => useTemplate(event.target.value)}><option value="">Usar plantilla…</option>{templates.map((template) => <option key={template.id} value={template.id}>{template.title}</option>)}</select>
        <input required value={form.title} onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))} placeholder="Asunto" />
        <textarea required value={form.body} onChange={(event) => setForm((prev) => ({ ...prev, body: event.target.value }))} placeholder="Mensaje educativo" />
        <input value={form.link_path || ''} onChange={(event) => setForm((prev) => ({ ...prev, link_path: event.target.value }))} placeholder="/learn" />
        <button className="forge-button primary" disabled={busy} type="submit">{busy ? 'Enviando…' : selected.length ? `Enviar a ${selected.length}` : 'Enviar a todo el grupo'}</button>
      </form>
      {group.notifications?.length > 0 && <div className="team-message-history"><h3>Mensajes recientes</h3>{group.notifications.map((item) => <article key={item.id}><div><strong>{item.title}</strong><small>@{item.sender} · {new Date(item.created_at).toLocaleDateString('es-MX')}</small></div><span>{item.read_count}/{item.recipient_count} leídos</span></article>)}</div>}
      {message && <div className={`admin-message ${message.type}`}>{message.text}</div>}
    </section>
  );
}
